import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaSearch, FaPhone } from 'react-icons/fa';
import ContactModal from './ContactModal';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Results', path: '/results' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'Admin', path: '/admin/dashboard' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const handleGetStartedClick = () => {
    setIsModalOpen(true);
    setIsOpen(false); // Close mobile menu if open
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 safe-area ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 touch-friendly">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg sm:text-xl">S</span>
              </div>
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                SEOExpert
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`font-medium transition-colors duration-200 touch-friendly ${
                    location.pathname === item.path
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <button 
                onClick={handleGetStartedClick}
                className="btn-primary touch-friendly"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Contact Button */}
            <div className="flex items-center space-x-2 lg:hidden">
              <a 
                href="tel:+919897785402"
                className="p-2 bg-primary text-white rounded-lg touch-friendly"
                aria-label="Call now"
              >
                <FaPhone className="w-4 h-4" />
              </a>
              <button
                className="p-2 touch-friendly"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <FaTimes className="w-6 h-6 text-gray-900" />
                ) : (
                  <FaBars className="w-6 h-6 text-gray-900" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
          <AnimatePresence>
            {isOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                  onClick={() => setIsOpen(false)}
                />
                
                {/* Mobile Menu */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="lg:hidden bg-white border-t border-gray-200 relative z-50"
                >
                  <div className="py-4 space-y-2">
                    {navItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className={`block px-4 py-3 font-medium transition-colors duration-200 touch-friendly ${
                          location.pathname === item.path
                            ? 'text-primary bg-blue-50 border-r-4 border-primary'
                            : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                    
                    {/* Contact Info in Mobile Menu */}
                    <div className="px-4 py-4 border-t border-gray-200 mt-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <FaPhone className="text-primary w-4 h-4" />
                          <a 
                            href="tel:+919897785402"
                            className="text-gray-700 hover:text-primary"
                          >
                            +91-9897785402
                          </a>
                        </div>
                        <div className="flex items-center space-x-3">
                          <FaSearch className="text-primary w-4 h-4" />
                          <a 
                            href="mailto:maheshsharma4651@gmail.com"
                            className="text-gray-700 hover:text-primary"
                          >
                            maheshsharma4651@gmail.com
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    <div className="px-4 pt-4">
                      <button 
                        onClick={handleGetStartedClick}
                        className="btn-primary w-full touch-friendly"
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};

export default Navbar; 